import { useCallback, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { useNotifications } from '@/providers/notificationHooks';

type EntityWithProject = {
  id: string;
  projectId: string;
};

type EntityDetailLoadState<TEntity> = {
  data: TEntity | null | undefined;
  isPending: boolean;
  isFetched: boolean;
  hasInvalidParams?: boolean;
  additionalNotFound?: boolean;
};

type EntityDetailDeleteConfig = {
  mutation: {
    mutateAsync: (input: { id: string; projectId: string }) => Promise<unknown>;
    isPending: boolean;
  };
  successMessage: string;
  fallbackErrorMessage: string;
};

type EntityDetailRedirectConfig = {
  onDeleteTo: string;
  notFoundTo?: string;
  notFoundBackLabel: string;
};

type EntityDetailNotFoundCopy = {
  title: string;
  body: string;
};

type UseEntityDetailPageScaffoldOptions<TEntity extends EntityWithProject> = {
  load: EntityDetailLoadState<TEntity>;
  delete: EntityDetailDeleteConfig;
  redirects: EntityDetailRedirectConfig;
  notFoundCopy: EntityDetailNotFoundCopy;
};

type EntityDetailPageScaffoldState = {
  showNotFound: boolean;
  deleteLoading: boolean;
  renderNotFound: () => ReactElement;
  handleDelete: () => Promise<void>;
};

export const useEntityDetailPageScaffold = <TEntity extends EntityWithProject>({
  load,
  delete: deleteConfig,
  redirects,
  notFoundCopy,
}: UseEntityDetailPageScaffoldOptions<TEntity>): EntityDetailPageScaffoldState => {
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const notFoundTo = redirects.notFoundTo ?? redirects.onDeleteTo;
  const showNotFound =
    Boolean(load.hasInvalidParams) ||
    Boolean(load.additionalNotFound) ||
    (!load.isPending && load.isFetched && !load.data);

  const renderNotFound = useCallback(
    () => (
      <NotFoundScreen
        title={notFoundCopy.title}
        body={notFoundCopy.body}
        backTo={notFoundTo}
        backLabel={redirects.notFoundBackLabel}
      />
    ),
    [notFoundCopy.body, notFoundCopy.title, notFoundTo, redirects.notFoundBackLabel],
  );

  const handleDelete = useCallback(async (): Promise<void> => {
    if (!load.data) return;
    try {
      await deleteConfig.mutation.mutateAsync({ id: load.data.id, projectId: load.data.projectId });
      notify({ message: deleteConfig.successMessage, tone: 'green' });
      void navigate(redirects.onDeleteTo, { replace: true });
    } catch (deleteError) {
      const message =
        deleteError instanceof Error ? deleteError.message : deleteConfig.fallbackErrorMessage;
      notify({ message, tone: 'red' });
    }
  }, [
    deleteConfig.fallbackErrorMessage,
    deleteConfig.mutation,
    deleteConfig.successMessage,
    load.data,
    navigate,
    notify,
    redirects.onDeleteTo,
  ]);

  return {
    showNotFound,
    deleteLoading: deleteConfig.mutation.isPending,
    renderNotFound,
    handleDelete,
  };
};
